// Shipping estimate for a quote (CEO 2026-10-06). The rep confirms the
// ZIP (prefilled from the order's ship-to) and who ships each line; FedEx and
// UPS ground are rated live, and the result is added as the order's shipping
// line — charged at the carriers' PUBLISHED rate, our negotiated cost kept as
// the line's net cost. Re-running it updates the same line, never adds a
// second. The rep can still edit the line by hand afterwards.
import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

type Route = "ag_promo" | "cpg" | "huetone" | "universal" | "direct";
const ROUTE_LABEL: Record<Route, string> = {
  direct: "Supplier ships direct",
  universal: "Universal Promotions",
  cpg: "Custom Print Graphics",
  huetone: "Huetone",
  ag_promo: "AG Promo",
};
interface EstimateLine { lineId: string; name: string; quantity: number; route: Route | null; problem: string | null }
type Estimate =
  | { status: "estimated"; to: { postalCode: string; stateCode: string; residential: boolean }; lines: EstimateLine[]; published: number; cost: number; carrier: "fedex" | "ups"; service: string; transitDays: number | null; boxes: number }
  | { status: "needs_input" | "unavailable"; lines: EstimateLine[]; message: string };

/** Description the estimate writes; also how a re-run finds its own line. */
export const ESTIMATE_LINE_PREFIX = "Ground shipping to ";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export function ShippingEstimateModal(props: {
  orderId: string;
  /** The order's ship-to ZIP, if any. */
  shipToZip: string | null;
  /** An existing shipping line this estimate wrote before — updated, not duplicated. */
  existingLineId: string | null;
  onClose: () => void;
  onApplied: () => void;
}) {
  const { orderId, shipToZip, existingLineId, onClose, onApplied } = props;
  const [zip, setZip] = useState((shipToZip ?? "").slice(0, 5));
  const [residential, setResidential] = useState(false);
  const [routes, setRoutes] = useState<Record<string, Route>>({});
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  /** The inputs the shown estimate was rated for — Apply only when they
   *  still match what is on screen, so a stale price is never saved. */
  const [ratedFor, setRatedFor] = useState<string | null>(null);
  const latest = useRef<string | null>(null);
  type Inputs = { zip: string; residential: boolean; routes: Record<string, Route> };
  const keyOf = (v: Inputs) => JSON.stringify([v.zip, v.residential, Object.entries(v.routes).sort()]);
  const current = keyOf({ zip, residential, routes });

  const rate = useMutation({
    mutationFn: (v: Inputs) =>
      api<{ estimate: Estimate }>(`/orders/${orderId}/shipping-estimate`, {
        method: "POST",
        body: JSON.stringify({ postal_code: /^\d{5}$/.test(v.zip) ? v.zip : undefined, residential: v.residential, routes: v.routes }),
      }),
    onSuccess: (r, v) => {
      if (keyOf(v) !== latest.current) return; // a newer request superseded this one
      setEstimate(r.estimate);
      setRatedFor(keyOf(v));
    },
  });
  const rerate = (v: Inputs) => {
    latest.current = keyOf(v);
    setRatedFor(null);
    rate.mutate(v);
  };

  // Rate on open (from the order's own ship-to), and again whenever the rep
  // changes who ships a line, home/business, or finishes a 5-digit ZIP.
  useEffect(() => {
    if (zip.length === 0 || zip.length === 5) rerate({ zip, residential, routes });
  }, [zip, residential, routes]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = useMutation({
    mutationFn: async () => {
      if (estimate?.status !== "estimated") return;
      const body = {
        service_type: "shipping",
        description: `${ESTIMATE_LINE_PREFIX}${estimate.to.postalCode} — ${estimate.service}, ${estimate.boxes} box${estimate.boxes === 1 ? "" : "es"}`,
        unit_price: estimate.published.toFixed(2),
        net_cost: estimate.cost.toFixed(2),
        quantity_total: 1,
      };
      if (existingLineId) {
        await api(`/orders/${orderId}/lines/${existingLineId}`, { method: "PATCH", body: JSON.stringify(body) });
      } else {
        await api(`/orders/${orderId}/lines`, { method: "POST", body: JSON.stringify(body) });
      }
    },
    onSuccess: onApplied,
  });

  const busy = rate.isPending || apply.isPending;
  const fresh = ratedFor !== null && ratedFor === current;
  const error = rate.error ?? apply.error;
  const lines = estimate?.lines ?? [];
  const zipIncomplete = zip.length > 0 && zip.length < 5;

  // Modal behaviour (house idiom): focus moves in, Tab stays in, Escape
  // closes, focus returns to the button that opened it.
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const focusables = () => Array.from(panel.current?.querySelectorAll<HTMLElement>("button, input, select, a[href]") ?? []).filter((el) => !el.hasAttribute("disabled"));
    panel.current?.querySelector<HTMLInputElement>("#shipping-estimate-zip")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { if (!busyRef.current) { e.preventDefault(); closeRef.current(); } return; }
      if (e.key !== "Tab") return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0]!, last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); opener?.focus(); };
  }, []);

  const unassigned = lines.some((l) => !(routes[l.lineId] ?? l.route));

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end overflow-hidden bg-ink/30"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shipping-estimate-title"
    >
      {/* A side drawer (handoff W13): the order stays visible behind it. */}
      <div ref={panel} className="flex h-full w-full max-w-[560px] flex-col overflow-y-auto border-l border-rule bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-brown-50 px-6 py-4">
          <h2 id="shipping-estimate-title" className="font-display text-xl font-semibold text-brown-500">
            Estimate shipping
          </h2>
          
          <button type="button" onClick={onClose} disabled={busy} className="text-brown-300 hover:text-brown-500 disabled:opacity-50" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          className="flex-1 space-y-5 px-6 py-5"
          onSubmit={(e) => { e.preventDefault(); if (zip.length === 5) rerate({ zip, residential, routes }); }}
        >
          <div>
            <div className="flex flex-wrap items-end gap-4">
              <label htmlFor="shipping-estimate-zip" className="flex flex-col gap-1 text-sm font-semibold text-brown-500">
                Ships to ZIP
                <Input id="shipping-estimate-zip" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" className="w-28" placeholder="33434" aria-describedby="zip-help" />
              </label>
              <label className="flex items-center gap-2 pb-2 text-sm text-brown-500">
                <input type="checkbox" checked={residential} onChange={(e) => setResidential(e.target.checked)} />
                Delivering to a home
              </label>
            </div>
            <p id="zip-help" className="mt-1 text-xs text-brown-300">From the order's shipping address — change it to price another ZIP. Rates update as you change anything below.</p>
          </div>

          {lines.length > 0 ? (
            <div>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Who ships each line</p>
              {unassigned ? <p className="mb-2 text-xs text-rust-600">Choose who ships every line to get a price.</p> : null}
              <ul className="divide-y divide-brown-50 rounded-md border border-brown-100">
                {lines.map((l) => {
                  const chosen = routes[l.lineId] ?? l.route ?? "";
                  return (
                    <li key={l.lineId} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-brown-500">{l.name}</p>
                        <p className="text-xs text-brown-300">{l.quantity} pcs</p>
                        {l.problem && fresh ? <p className="text-xs font-semibold text-rust-600">{l.problem}</p> : null}
                      </div>
                      <select
                        aria-label={`Who ships ${l.name}`}
                        value={chosen}
                        onChange={(e) => setRoutes((r) => ({ ...r, [l.lineId]: e.target.value as Route }))}
                        className={`h-9 rounded-md border bg-white px-2 text-sm focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 ${chosen ? "border-brown-100 text-brown-500" : "border-rust-600 text-rust-600"}`}
                      >
                        <option value="" disabled>Choose who ships…</option>
                        {(Object.keys(ROUTE_LABEL) as Route[]).map((r) => <option key={r} value={r}>{ROUTE_LABEL[r]}</option>)}
                      </select>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {/* Always mounted, so screen readers announce what changes inside. */}
          <div aria-live="polite" className="min-h-[1.5rem]">
            {zipIncomplete ? (
              <p className="text-sm text-brown-300">Enter a 5-digit ZIP.</p>
            ) : rate.isPending ? (
              <p className="flex items-center gap-2 text-sm text-brown-300"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Getting FedEx and UPS rates…</p>
            ) : error ? (
              <p role="alert" className="text-sm text-rust-600">{error instanceof ApiError ? error.message : "Something went wrong. Try again."}</p>
            ) : estimate && estimate.status !== "estimated" && fresh ? (
              <p role="alert" className="text-sm font-semibold text-rust-600">{estimate.message}</p>
            ) : estimate?.status === "estimated" && fresh ? (
              <div className="rounded-md bg-peach-50 px-4 py-3 text-sm tabular-nums">
                <dl className="grid grid-cols-[1fr_auto] gap-y-1">
                  <dt className="font-semibold text-brown-500">Charge to customer</dt>
                  <dd className="text-right text-lg font-bold text-brown-500">{money(estimate.published)}</dd>
                  <dt className="text-brown-300">Our cost (negotiated)</dt>
                  <dd className="text-right text-brown-300">{money(estimate.cost)}</dd>
                </dl>
                <p className="mt-1 text-xs text-brown-300">
                  {estimate.service} · {estimate.boxes} box{estimate.boxes === 1 ? "" : "es"}{estimate.transitDays ? ` · about ${estimate.transitDays} days in transit` : ""}
                </p>
              </div>
            ) : null}
          </div>
        </form>

        <div className="flex justify-end gap-3 border-t border-brown-50 px-6 py-4">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="button" size="sm" onClick={() => apply.mutate()} disabled={busy || !fresh || estimate?.status !== "estimated"}>
            {apply.isPending ? "Saving…" : existingLineId ? "Update shipping line" : "Add shipping line"}
          </Button>
        </div>
      </div>
    </div>
  );
}
